/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ElementRef, Renderer2 } from '@angular/core';

import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * A generic class for adding a pulse style animation to an angular element.
 * The @angular/animations api is geared towards transitions, ie, moving from one
 * state to another. This doesn't really mesh nicely with a pulse animation
 * startingState -> animate -> startingState, something like a shake. So this exists.
 *
 * Preferred Usage:
 * - use [[PulseAnimationDirective]]
 *
 * Low Level Usage:
 * - create your animation trigger class in css (eventually you'll want to also import this)
 * - in your component where you want the animation
 *   - inject Renderer2
 *   - add an ElementRef for the element you want to animate
 *     - html: `#animationElement`
 *     - ts: `@ViewChild('animationElement', { read: ElementRef, static: true }) animationElement: ElementRef;`
 *   - create an instance of the animation in ngOnInit `this.shakeAnimation = new ShakeAnimation(this.renderer, this.animationElement);`
 *   - make sure it's destroyed appropriately `this.shakeAnimation.destroy();`
 *   - trigger the animation with either of the methods `pulse()` or `observe(ob)`
 *
 *
 */

export class PulseAnimation {
	private internalSubject = new Subject<void>();
	private shutdownSubject = new Subject<void>();

	constructor(private renderer: Renderer2, private element: ElementRef, showTime: number, className: string) {
		let startTimeout: any;
		let endTimeout: any;

		this.internalSubject.pipe(takeUntil(this.shutdownSubject)).subscribe(() => {
			clearTimeout(startTimeout);
			clearTimeout(endTimeout);

			this.renderer.removeClass(this.element.nativeElement, className);
			startTimeout = setTimeout(() => {
				this.renderer.addClass(this.element.nativeElement, className);
			}, 5);
			endTimeout = setTimeout(() => {
				this.renderer.removeClass(this.element.nativeElement, className);
			}, showTime);
		});

		//clean up and reset if destroyed mid animation
		this.shutdownSubject.subscribe(() => {
			clearTimeout(startTimeout);
			clearTimeout(endTimeout);

			if (this.element && this.element.nativeElement) {
				this.renderer.removeClass(this.element.nativeElement, className);
			}
		});
	}

	/** trigger one instance of an animation pulse */
	public pulse() {
		this.internalSubject.next();
	}
	/**
	 * Add this observable as something to trigger the animation. Will be unsubscribed
	 * on shutdown, can unsub per-observable by completing the one that was passed in.
	 * @param  ob$ Observable of any type, the value is not used, just the emission
	 */
	public observe(ob$: Observable<any>) {
		ob$.pipe(takeUntil(this.shutdownSubject)).subscribe(() => this.internalSubject.next());
	}

	/** clear out all the data/ obserables */
	public destroy() {
		this.shutdownSubject.next();
		this.internalSubject.complete();
	}
}
